import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Download,
  Filter,
  Search,
  Loader2,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowLeft,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { isAgencyOwnerRole } from "@/lib/roles";
import { useCurrentTramsContext } from "@/hooks/useTramsAccounts";
import { TramsAccountSwitcher } from "@/components/trams/TramsAccountSwitcher";

import {
  SectionErrorBoundary,
  SectionScopeContext,
  SectionLoadError,
} from "@/components/invoicing/SectionErrorBoundary";
import type { SectionScopeInfo } from "@/components/invoicing/SectionErrorBoundary";
import {
  useStatementList,
  useStatementDetail,
  useStatementYears,
} from "@/hooks/useStatements";
import { formatTramsInvoiceDisplayNumber } from "@shared/invoice-display";
import { statementPdfFilename } from "@/lib/pdfFilename";
import type {
  StatementSummaryRow,
  StatementInvoiceRow,
} from "@/hooks/useStatements";

const BARE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function formatStatementDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function formatCurrency(value: string | null | undefined): string {
  const num = parseFloat(value || "0");
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

/** Matches statement PDF rate column (numeric percent, no % suffix). */
function formatAgentRate(rate: string | number | null | undefined): string {
  if (rate == null) return "—";
  const numericRate = typeof rate === "string" ? Number(rate) : rate;
  if (!Number.isFinite(numericRate)) return "—";
  return numericRate.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * The date to show under "Paid" for a statement line.
 *
 * `reportDate` is only a payment date on the Comm Track arm — the contract in
 * `server-nest/.../lambda-api.client.ts` is explicit that it carries
 * INVOICE.ISSUEDATE on the ARC and Supplier arms, and "must never be rendered
 * as one". So the fallback applies to Comm Track and nowhere else; an ARC line
 * with no `paidAt` reads as unpaid rather than as paid on its issue date.
 */
function statementPaidDate(inv: StatementInvoiceRow): string | null {
  if (inv.paidAt) return inv.paidAt;
  return inv.section === "Comm Track" ? inv.reportDate ?? null : null;
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  const normalized = BARE_DATE_RE.test(dateStr)
    ? `${dateStr}T00:00:00Z`
    : dateStr;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return "—";
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

function useStatementPdfDownload() {
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = useCallback(
    async (
      statementDate: string,
      params: { tramsAccountId?: number; viewMode?: "my" | "all" },
    ) => {
      setDownloading(true);
      try {
        const qs = new URLSearchParams();
        if (params.tramsAccountId)
          qs.set("tramsAccountId", String(params.tramsAccountId));
        if (params.viewMode) qs.set("viewMode", params.viewMode);

        const res = await fetch(
          `/api/statements/${statementDate}/pdf?${qs.toString()}`,
          { credentials: "include" },
        );

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || "Failed to generate PDF");
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = statementPdfFilename(statementDate);
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("PDF download failed:", err);
        throw err;
      } finally {
        setDownloading(false);
      }
    },
    [],
  );

  return { downloadPdf, downloading };
}

function StatementDetailView({
  statementDate,
  tramsAccountId,
  viewMode,
  onBack,
}: {
  statementDate: string;
  tramsAccountId?: number;
  viewMode: "my" | "all";
  onBack: () => void;
}) {
  const { data, isLoading, error, refetch } = useStatementDetail(
    statementDate,
    { tramsAccountId, viewMode },
  );
  const { downloadPdf, downloading } = useStatementPdfDownload();
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleDownloadPdf = useCallback(async () => {
    setPdfError(null);
    try {
      await downloadPdf(statementDate, { tramsAccountId, viewMode });
    } catch (err) {
      setPdfError(
        err instanceof Error ? err.message : "Failed to generate PDF",
      );
    }
  }, [downloadPdf, statementDate, tramsAccountId, viewMode]);

  if (error) {
    return (
      <SectionLoadError
        sectionName="Statement Detail"
        error={error}
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-center p-16" role="status">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="sr-only">Loading statement</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Statements
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadPdf}
          disabled={downloading}
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-1.5" />
          )}
          {downloading ? "Generating..." : "Download PDF"}
        </Button>
      </div>

      {pdfError && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
          {pdfError}
        </div>
      )}

      {data.inProgress && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          This week is still accumulating. Totals are not final until Friday.
        </div>
      )}

      {/* Statement Metadata */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">
            {formatStatementDate(data.statementDate)} Statement
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Statement Date
              </p>
              <p className="text-sm font-medium mt-1">
                {formatStatementDate(data.statementDate)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Invoice Count
              </p>
              <p className="text-sm font-medium mt-1">{data.invoiceCount}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Gross Commission
              </p>
              <p className="text-sm font-medium mt-1">
                {formatCurrency(data.totalCommission)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Total Sale Amount
              </p>
              <p className="text-sm font-medium mt-1">
                {formatCurrency(data.totalFare)}
              </p>
            </div>
            {data.agentCommission !== undefined && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Net Commission
                </p>
                <p className="text-sm font-medium mt-1">
                  {formatCurrency(data.agentCommission)}
                </p>
              </div>
            )}
            {data.adjustmentTotal !== undefined && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Adjustments
                </p>
                <p className="text-sm font-medium mt-1">
                  {formatCurrency(data.adjustmentTotal)}
                </p>
              </div>
            )}
            {data.netPayout !== undefined && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Net Payout
                </p>
                <p className="text-sm font-medium mt-1">
                  {formatCurrency(data.netPayout)}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              {/* AR-316: below `md` only Traveler + Agent Commission stay as
                  columns; the rest move into a stacked summary inside the
                  traveler cell. This table needed 805px of sideways scrolling on
                  a phone, and the commission columns — the whole point of a
                  statement — were the ones off-screen. */}
              <TableRow className="bg-gray-50 dark:bg-gray-800/50">
                <TableHead className="hidden md:table-cell">Invoice #</TableHead>
                <TableHead className="hidden md:table-cell">Trip ID</TableHead>
                <TableHead>Primary Traveler</TableHead>
                <TableHead className="hidden md:table-cell">Supplier</TableHead>
                <TableHead className="hidden md:table-cell">Type</TableHead>
                <TableHead className="hidden text-right md:table-cell">Total Sale Amount</TableHead>
                <TableHead className="hidden text-right md:table-cell">Gross Comm.</TableHead>
                <TableHead className="hidden text-right md:table-cell">Rate</TableHead>
                <TableHead className="text-right">Net Comm.</TableHead>
                <TableHead className="hidden md:table-cell">Date Paid</TableHead>
                {data.fromTrams && (
                  <TableHead className="hidden md:table-cell">AIVIA Match</TableHead>
                )}
                {viewMode === "all" && (
                  <TableHead className="hidden md:table-cell">Advisor</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.invoices.map((inv: StatementInvoiceRow) => (
                <TableRow key={inv.rowKey ?? inv.id}>
                  <TableCell className="hidden md:table-cell">
                    {formatTramsInvoiceDisplayNumber(inv.tramsInvoiceNumber)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{inv.recordLocator ?? "—"}</TableCell>
                  <TableCell className="font-medium">
                    <span className="block">{inv.primaryTraveler ?? "—"}</span>
                    <span className="mt-1 flex flex-col gap-0.5 text-xs font-normal text-muted-foreground md:hidden">
                      <span className="flex flex-wrap items-center gap-x-1.5">
                        <span>{formatTramsInvoiceDisplayNumber(inv.tramsInvoiceNumber)}</span>
                        {inv.recordLocator && <span aria-hidden>·</span>}
                        {inv.recordLocator && <span>{inv.recordLocator}</span>}
                      </span>
                      <span>{inv.supplierName ?? "—"}{inv.travelType ? ` · ${inv.travelType}` : ""}</span>
                      <span className="whitespace-nowrap">
                        Sale {formatCurrency(inv.totalFare)} · Gross Comm. {formatCurrency(inv.commissionAmount)}
                      </span>
                      <span className="whitespace-nowrap">
                        Rate {formatAgentRate(inv.agentRatePercent)} · Paid{" "}
                        {formatShortDate(statementPaidDate(inv))}
                      </span>
                      {viewMode === "all" && (inv.sellingAgentName ?? inv.advisorName) && (
                        <span className="truncate">
                          {inv.sellingAgentName ?? inv.advisorName}
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{inv.supplierName ?? "—"}</TableCell>
                  <TableCell className="hidden md:table-cell">{inv.travelType ?? "—"}</TableCell>
                  <TableCell className="hidden text-right md:table-cell">
                    {formatCurrency(inv.totalFare)}
                  </TableCell>
                  <TableCell className="hidden text-right font-medium md:table-cell">
                    {formatCurrency(inv.commissionAmount)}
                  </TableCell>
                  <TableCell className="hidden text-right md:table-cell">
                    {formatAgentRate(inv.agentRatePercent)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(inv.agentCommissionAmount)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {formatShortDate(statementPaidDate(inv))}
                  </TableCell>
                  {data.fromTrams && (
                    <TableCell className="hidden md:table-cell">
                      <span
                        className={
                          inv.aiviaActionsEnabled
                            ? "text-xs text-muted-foreground"
                            : "text-xs font-medium text-amber-700 dark:text-amber-400"
                        }
                        title={
                          inv.aiviaActionsEnabled
                            ? "Matched by TRAMS invoice, booking, and agent booking"
                            : "No AIVIA invoice match; invoice actions are disabled"
                        }
                      >
                        {inv.aiviaActionsEnabled ? "Matched" : "TRAMS only"}
                      </span>
                    </TableCell>
                  )}
                  {viewMode === "all" && (
                    <TableCell className="hidden md:table-cell">
                      {/* The seller, not the statement's owner: in the team view
                          every line would otherwise read as the agency owner. */}
                      {inv.sellingAgentName ?? inv.advisorName ?? "—"}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatementListView({
  tramsAccountId,
  viewMode,
}: {
  tramsAccountId?: number;
  viewMode: "my" | "all";
}) {
  const [page, setPage] = useState(1);
  const [filterYear, setFilterYear] = useState<number | undefined>(undefined);
  const [filterMonth, setFilterMonth] = useState<number | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState<string | null>(
    null,
  );
  const { downloadPdf, downloading } = useStatementPdfDownload();
  const [downloadingDate, setDownloadingDate] = useState<string | null>(null);
  const pageSize = 20;
  const { data: yearsData } = useStatementYears({ tramsAccountId, viewMode });
  const yearOptions = yearsData?.years ?? [];

  useEffect(() => {
    setSelectedStatement(null);
    setPage(1);
    // Year options are scoped to viewMode/tramsAccountId — clear any selected
    // year so the filter can't persist a year missing from the new option set.
    setFilterYear(undefined);
  }, [viewMode, tramsAccountId]);

  const { data, isLoading, error, refetch } = useStatementList({
    page,
    pageSize,
    sortDir: "desc",
    tramsAccountId,
    viewMode,
    year: filterYear,
    month: filterMonth,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter((s: StatementSummaryRow) =>
      formatStatementDate(s.statementDate).toLowerCase().includes(q),
    );
  }, [items, searchQuery]);

  const hasActiveFilters =
    filterYear !== undefined || filterMonth !== undefined;

  const clearFilters = useCallback(() => {
    setFilterYear(undefined);
    setFilterMonth(undefined);
    setPage(1);
    setFilterOpen(false);
  }, []);

  if (selectedStatement) {
    return (
      <StatementDetailView
        statementDate={selectedStatement}
        tramsAccountId={tramsAccountId}
        viewMode={viewMode}
        onBack={() => setSelectedStatement(null)}
      />
    );
  }

  if (error) {
    return (
      <SectionLoadError
        sectionName="Statements"
        error={error}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center justify-end gap-2">
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-xs text-muted-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Clear filters
          </Button>
        )}
        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon" aria-label="Statement filters"
              className={`h-9 w-9 ${hasActiveFilters ? "border-primary text-primary" : ""}`}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64" align="end" aria-labelledby="stmt-filter-title">
            <div className="space-y-3">
              <p id="stmt-filter-title" className="text-sm font-medium">Filter Statements</p>
              <div>
                <label htmlFor="stmt-filter-year" className="text-xs text-muted-foreground">Year</label>
                <Select
                  value={filterYear?.toString() ?? "all"}
                  onValueChange={(v) => {
                    setFilterYear(v === "all" ? undefined : parseInt(v, 10));
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="stmt-filter-year" className="h-8 mt-1">
                    <SelectValue placeholder="All years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All years</SelectItem>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="stmt-filter-month" className="text-xs text-muted-foreground">Month</label>
                <Select
                  value={filterMonth?.toString() ?? "all"}
                  onValueChange={(v) => {
                    setFilterMonth(v === "all" ? undefined : parseInt(v, 10));
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="stmt-filter-month" className="h-8 mt-1">
                    <SelectValue placeholder="All months" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All months</SelectItem>
                    {MONTH_OPTIONS.map((m) => (
                      <SelectItem key={m.value} value={m.value.toString()}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={clearFilters}
                >
                  Clear filters
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Search statements"
            placeholder="Search statements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 w-[220px]"
          />
        </div>
      </div>

      {/* Statement list — AR-210 (TC-14.1, TC-15.1, TC-29.1): loading and the
          number of statements shown are announced to screen readers. */}
      <p className="sr-only" role="status" aria-live="polite">
        {isLoading ? "Loading statements" : `${filteredItems.length} statement${filteredItems.length === 1 ? "" : "s"} shown`}
      </p>
      <Card aria-busy={isLoading || undefined}>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-16 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-medium">
                {hasActiveFilters || searchQuery
                  ? "No statements match your filters"
                  : "No statements yet"}
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                {hasActiveFilters || searchQuery
                  ? "Try adjusting your filters or search term."
                  : "Statements will appear here once a paid week is ready, typically Friday morning."}
              </p>
              {(hasActiveFilters || searchQuery) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    clearFilters();
                    setSearchQuery("");
                  }}
                >
                  Clear all filters
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filteredItems.map((stmt: StatementSummaryRow) => (
                <div
                  key={stmt.statementDate}
                  className="flex items-center justify-between px-6 py-5 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedStatement(stmt.statementDate)}
                      className="text-base font-medium underline underline-offset-2 decoration-1 hover:text-primary transition-colors text-left"
                    >
                      {formatStatementDate(stmt.statementDate)} Statement
                    </button>
                    {stmt.inProgress && (
                      <Badge variant="secondary">In progress</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-medium">
                        Gross{" "}
                        {formatCurrency(
                          stmt.grossCommission ?? stmt.totalCommission,
                        )}
                      </p>
                      {stmt.netPayout !== undefined && (
                        <p className="text-xs font-medium">
                          Net {formatCurrency(stmt.netPayout)}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {stmt.invoiceCount} invoice
                        {stmt.invoiceCount !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-muted-foreground hover:text-foreground"
                      disabled={
                        downloading && downloadingDate === stmt.statementDate
                      }
                      onClick={async (e) => {
                        e.stopPropagation();
                        setDownloadingDate(stmt.statementDate);
                        try {
                          await downloadPdf(stmt.statementDate, {
                            tramsAccountId,
                            viewMode,
                          });
                        } catch {
                          // error handled in hook
                        } finally {
                          setDownloadingDate(null);
                        }
                      }}
                      title="Download PDF"
                      aria-label={`Download PDF, ${formatStatementDate(stmt.statementDate)} statement`}
                    >
                      {downloading && downloadingDate === stmt.statementDate ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Download className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 &&
        !isLoading &&
        filteredItems.length > 0 &&
        (() => {
          const displayTotal = searchQuery ? filteredItems.length : totalCount;
          const start = displayTotal === 0 ? 0 : (page - 1) * pageSize + 1;
          const end = Math.min(page * pageSize, displayTotal);
          return (
            <div className="flex items-center justify-between gap-2 px-1 max-sm:justify-start">
              <p className="text-sm text-muted-foreground" aria-live="polite" aria-atomic="true">
                Showing {start}–{end} of {displayTotal} statements
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon" aria-label="First page"
                  className="h-8 w-8"
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon" aria-label="Previous page"
                  className="h-8 w-8"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-3 tabular-nums">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon" aria-label="Next page"
                  className="h-8 w-8"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon" aria-label="Last page"
                  className="h-8 w-8"
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

export default function Statements() {
  const { user } = useAuth();
  const { impersonation } = useImpersonation();
  const { effectiveRole, hasCompanyAccess } = useCompanyAccess();
  const { data: tramsData } = useCurrentTramsContext();
  const currentTramsAccountId = tramsData?.context?.tramsAccountId;

  const viewMode: "my" | "all" = hasCompanyAccess ? "all" : "my";

  const scopeContext = useMemo<SectionScopeInfo>(
    () => ({
      memberId: impersonation.active
        ? (impersonation.targetUserId ?? user?.id)
        : user?.id,
      tramsAccountId: currentTramsAccountId,
      role: effectiveRole ?? undefined,
      viewMode,
    }),
    [
      impersonation.active,
      impersonation.targetUserId,
      user?.id,
      effectiveRole,
      currentTramsAccountId,
      viewMode,
    ],
  );

  return (
    <SectionScopeContext.Provider value={scopeContext}>
      <div className="p-4 sm:p-6 max-w-[1600px] mx-auto space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Invoice Statements
            </h1>
            {/* AR-316 — v9 subtitle: what the page covers, on one line. */}
            <p className="text-sm text-muted-foreground mt-0.5">
              {hasCompanyAccess ? "All advisor statements" : "Your statements"} ·
              your share, after adjustments
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <TramsAccountSwitcher disabled={viewMode === "all" && !isAgencyOwnerRole(effectiveRole)} />
          </div>
        </div>

        <SectionErrorBoundary sectionName="Statement List">
          <StatementListView
            tramsAccountId={
              viewMode === "all" && !isAgencyOwnerRole(effectiveRole)
                ? undefined
                : currentTramsAccountId
            }
            viewMode={viewMode}
          />
        </SectionErrorBoundary>
      </div>
    </SectionScopeContext.Provider>
  );
}
